import React from 'react';
import styled from 'styled-components';

// Styled Components

/**
 * main hero container
 * @memberof HeroImage
 * @type {styled-component}
 * @property {string} [$imgUrl] - the URL towards the image that will be used as the background. If not provided the background will be black.
 * @property {string} [$backgroundPosition] - the value of the background-position rule (default: center).
 */
const HeroContainer = styled.div`
  display: flex;
  width: 100%;
  height: var(--spectrum-global-dimension-size-3000);
  padding: 0;
  border-radius: var(--spectrum-global-dimension-size-50);
  background-image: ${({ $imgUrl }) => `image-set(
      url(/${$imgUrl.webp.replace(/^\//, ``)}) type("image/webp"),
      url(/${$imgUrl.png.replace(/^\//, '')}) type("image/png")
    )`};
  background-color: #161217;
  background-size: cover;
  background-position: ${({ $backgroundPosition }) => $backgroundPosition ?? 'center'};
  box-sizing: border-box;
`;

/**
 * banner content container element
 * @memberof LinkBanner
 * @type {styled-component}
 */
const BannerContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: var(--spectrum-global-dimension-static-size-50);
  padding: var(--spectrum-global-dimension-static-size-400);
  color: var(--spectrum-global-color-gray-50);
  width: 100%;
  height: 100%;
  box-sizing: border-box;
`;

/**
 * title element
 * @memberof HeroImage
 * @type {styled-component}
 */
const Title = styled.h1`
  font-size: var(--spectrum-global-dimension-font-size-700);
  margin: 0 0 var(--spectrum-global-dimension-size-100) 0;
  font-weight: bold;
  text-align: center;
`;

/**
 * subtitle element, built upon the Title element
 * @memberof HeroImage
 * @type {styled-component}
 */
const Subtitle = styled.p`
  margin: 0 0 var(--spectrum-global-dimension-size-100) 0;
  font-size: var(--spectrum-global-dimension-font-size-300);
  font-weight: bold;
`;

/**
 * Hero image to display important information
 * @component
 * @category Featured
 * @prop {string} imgUrl - The URL of the image that will be used in the background. If not set the background will be black.
 * @prop {string} title - Title text
 * @prop {string} subtitle - Subtitle text
 * @prop {string} [imgBgPosition] - The value of the background-position rule of the container (default: center).
 * @returns JSX.Element
 * @example
 * return (
 *    <HeroImage imgUrl="https://d29fhpw069ctt2.cloudfront.net/vector/5105/preview/preview-1400x1082.jpg" title="Explore the Adobe App Marketplaces" subtitle="Thousands of apps to enhance your experience with products across all Adobe Clouds." />
 * )
 */
const HeroImage = ({ imgUrl, title, subtitle, imgBgPosition }) => {
  return (
    <HeroContainer $imgUrl={imgUrl} $backgroundPosition={imgBgPosition}>
      <BannerContentContainer>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </BannerContentContainer>
    </HeroContainer>
  );
};

export default HeroImage;
